import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "@/api";
import { toast } from "sonner";
import { MapShell } from "@/components/MapShell";
import { Globe, Image as ImageIcon, Upload, RotateCcw } from "lucide-react";

export function MapSettings({ canWrite }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const cornersRef = useRef([]);
  const modeRef = useRef("osm");
  const [mode, setMode] = useState("osm");
  const [image, setImage] = useState(null);
  const [bounds, setBounds] = useState(null); // [[s,w],[n,e]]
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [highlightNearest, setHighlightNearest] = useState(false);
  const [autoApproach, setAutoApproach] = useState(true);
  const [approachEnabled, setApproachEnabled] = useState(true);
  const [monitorRepeat, setMonitorRepeat] = useState(0);
  const [monitorIdle, setMonitorIdle] = useState("time");
  const [flLeft, setFlLeft] = useState("");
  const [flRight, setFlRight] = useState("");
  const [monShowFl, setMonShowFl] = useState(false);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    api.get("/settings/map").then(({ data }) => {
      setMode(data.mode || "osm");
      setImage(data.static_image || null);
      setBounds(data.bounds || null);
      setHighlightNearest(!!data.highlight_nearest_route);
      setAutoApproach(data.auto_approach_lines !== false);
      setApproachEnabled(data.approach_routes_enabled !== false);
      setMonitorRepeat(Number(data.monitor_repeat_seconds) || 0);
      setMonitorIdle(data.monitor_idle_mode || "time");
      setFlLeft(data.flightroute_left_name || "");
      setFlRight(data.flightroute_right_name || "");
      setMonShowFl(!!data.monitor_show_flightroute);
      setReady(true);
    }).catch(() => setReady(true));
  }, []);

  // Init the picker map once
  useEffect(() => {
    if (!ready || !el.current || mapRef.current) return;
    const map = L.map(el.current, { attributionControl: false }).setView([52.11, 5.18], 13);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    if (bounds) { try { map.fitBounds(bounds); } catch (e) {} }

    if (canWrite) {
      map.on("click", (e) => {
        if (modeRef.current !== "static") return;
        const c = cornersRef.current;
        if (c.length >= 2) { c.forEach((m) => map.removeLayer(m)); cornersRef.current = []; }
        const marker = L.marker(e.latlng).addTo(map);
        cornersRef.current.push(marker);
        if (cornersRef.current.length === 2) {
          const [a, b] = cornersRef.current.map((m) => m.getLatLng());
          const s = Math.min(a.lat, b.lat), n = Math.max(a.lat, b.lat);
          const w = Math.min(a.lng, b.lng), ee = Math.max(a.lng, b.lng);
          setBounds([[Number(s.toFixed(6)), Number(w.toFixed(6))], [Number(n.toFixed(6)), Number(ee.toFixed(6))]]);
        }
      });
    }
  }, [ready, mode]); // eslint-disable-line

  // Live preview of the static overlay. Show the uploaded image immediately —
  // even before the area (bounds) is drawn — across the current map view so the
  // admin can see what they uploaded and then click 2 corners to georeference it.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (overlayRef.current) { map.removeLayer(overlayRef.current); overlayRef.current = null; }
    if (mode === "static" && image) {
      const bb = bounds || map.getBounds().pad(-0.15);
      overlayRef.current = L.imageOverlay(image, bb, { opacity: 0.8 }).addTo(map);
      if (bounds) { try { map.fitBounds(bounds); } catch (e) {} }
    }
  }, [mode, image, bounds]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Afbeelding te groot (max 5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const resetCorners = () => {
    const map = mapRef.current;
    cornersRef.current.forEach((m) => map && map.removeLayer(m));
    cornersRef.current = [];
    setBounds(null);
  };

  const save = async () => {
    if (mode === "static" && (!image || !bounds)) {
      toast.error("Upload een kaartafbeelding én teken het gebied (klik 2 hoeken)");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/map", {
        mode,
        static_image: mode === "static" ? image : null,
        bounds: mode === "static" ? bounds : null,
        highlight_nearest_route: highlightNearest,
        auto_approach_lines: autoApproach,
        approach_routes_enabled: approachEnabled,
        monitor_repeat_seconds: Number(monitorRepeat) || 0,
        monitor_idle_mode: monitorIdle,
        flightroute_left_name: flLeft,
        flightroute_right_name: flRight,
        monitor_show_flightroute: monShowFl,
      });
      toast.success("Kaartinstelling opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const Opt = ({ value, icon: Icon, title, desc }) => (
    <button type="button" data-testid={`map-mode-${value}`} disabled={!canWrite} onClick={() => setMode(value)}
      className={`flex flex-1 items-start gap-3 rounded-xl border p-3 text-left transition-colors ${mode === value ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"} ${!canWrite ? "opacity-60" : ""}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${mode === value ? "text-blue-600" : "text-slate-400"}`} />
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </button>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="map-settings">
      <div className="mb-3 text-sm font-bold text-slate-900">Kaart-ondergrond</div>
      <p className="mb-3 text-xs text-slate-500">
        Kies de ondergrond voor alle kaarten. Met een statische kaart blijft het intekenen van het verzorgingsgebied en het plaatsen van gebouwen/bluswater gewoon werken. De satellietknop op de kaarten legt satellietbeeld over het gebied.
      </p>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Opt value="osm" icon={Globe} title="Live kaart (OpenStreetMap)" desc="Standaard online kaartweergave" />
        <Opt value="static" icon={ImageIcon} title="Statische kaart" desc="Eigen kaartafbeelding als ondergrond" />
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="monitor-flightroute-settings">
        <div className="mb-1 text-sm font-semibold text-slate-800">KazerneMonitor — vliegroute</div>
        <p className="mb-3 text-xs text-slate-500">De idle-modus (tijd/taken) stel je per kazernemonitor in bij <span className="font-semibold">Beheer → Apparaten</span>.</p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="monitor-show-flightroute" disabled={!canWrite} checked={monShowFl} onChange={(e) => setMonShowFl(e.target.checked)} className="h-4 w-4 accent-blue-600" />
          Actieve vliegroute op de monitor tonen (in idle)
        </label>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className="text-xs font-semibold text-slate-500">Naam vliegroute links
            <input data-testid="flightroute-left-name" disabled={!canWrite} value={flLeft} onChange={(e) => setFlLeft(e.target.value)} placeholder="Vliegroute links" className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-800" /></label>
          <label className="text-xs font-semibold text-slate-500">Naam vliegroute rechts
            <input data-testid="flightroute-right-name" disabled={!canWrite} value={flRight} onChange={(e) => setFlRight(e.target.value)} placeholder="Vliegroute rechts" className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-800" /></label>
        </div>
      </div>

      <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="map-approach-enabled-row">
        <input type="checkbox" data-testid="map-approach-enabled" disabled={!canWrite} checked={approachEnabled} onChange={(e) => setApproachEnabled(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
        <span>
          <span className="block text-sm font-semibold text-slate-800">Aanrijroutes-tekentool tonen</span>
          <span className="block text-xs text-slate-500">Toont in de melding de knop &ldquo;Aanrijlijnen&rdquo; en het toewijzen van aanrijzijden per voertuig. Zet dit uit als je alleen het vakje &ldquo;tweezijdig aanrijden&rdquo; met de waarschuwing wilt gebruiken — zonder routes op de kaart.</span>
        </span>
      </label>

      <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <input type="checkbox" data-testid="map-highlight-nearest" disabled={!canWrite} checked={highlightNearest} onChange={(e) => setHighlightNearest(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
        <span>
          <span className="block text-sm font-semibold text-slate-800">Dichtstbijzijnde aanrijroute markeren</span>
          <span className="block text-xs text-slate-500">In de Voertuig-app wordt bij &ldquo;tweezijdig aanrijden&rdquo; de route die het dichtst bij de actuele voertuigpositie ligt groen uitgelicht. Werkt met live én statische kaart (o.b.v. GPS-coördinaten).</span>
        </span>
      </label>

      <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <input type="checkbox" data-testid="map-auto-approach" disabled={!canWrite} checked={autoApproach} onChange={(e) => setAutoApproach(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
        <span>
          <span className="block text-sm font-semibold text-slate-800">Aanrijzijden automatisch tonen bij tweezijdig</span>
          <span className="block text-xs text-slate-500">Toont bij &ldquo;tweezijdig aanrijden&rdquo; automatisch twee indicatieve aanrijlijnen op de kaart. Zet dit uit als je alleen zelf-getekende aanrijlijnen wilt tonen.</span>
        </span>
      </label>

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <label className="block text-sm font-semibold text-slate-800">Herhaal-omroep Kazernemonitor</label>
        <p className="mb-2 text-xs text-slate-500">Herhaalt een openstaande melding elke X seconden tot een voertuig heeft bevestigd. 0 = uit.</p>
        <div className="flex items-center gap-2">
          <input data-testid="monitor-repeat-secs" type="number" min="0" max="600" disabled={!canWrite} value={monitorRepeat} onChange={(e) => setMonitorRepeat(e.target.value)} className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <span className="text-sm text-slate-500">seconden</span>
        </div>
      </div>

      {mode === "static" && (
        <div className="space-y-3" data-testid="static-map-config">
          <div className="flex flex-wrap items-center gap-2">
            {canWrite && (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                <Upload className="h-4 w-4" /> Kaartafbeelding uploaden
                <input data-testid="map-image-upload" type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
            )}
            {image && <span className="text-xs font-medium text-emerald-600">✓ afbeelding geladen</span>}
            {canWrite && bounds && (
              <button data-testid="map-reset-corners" onClick={resetCorners} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                <RotateCcw className="h-3.5 w-3.5" /> Gebied opnieuw tekenen
              </button>
            )}
          </div>
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {bounds ? "Gebied ingesteld. Sla op om te bevestigen." : "Klik op de kaart op 2 tegenoverliggende hoeken om aan te geven waar de afbeelding valt."}
          </div>
          <MapShell heightClass="h-[380px]" testid="map-settings-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
            <div ref={el} data-testid="map-settings-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
          </MapShell>
        </div>
      )}

      {canWrite && (
        <div className="mt-4 flex justify-end">
          <button data-testid="map-settings-save" onClick={save} disabled={saving}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      )}
    </div>
  );
}
